/**
 * Hook useEntriesMain
 *
 * Hook centralizado que maneja toda la lógica de negocio del módulo MerchandiseV3/List.
 * Sigue el patrón establecido en Reused/List para consistencia en la arquitectura.
 *
 * Responsabilidades:
 * - Manejo de filtros, búsqueda y sorting
 * - Orquestación de datos desde useEntries
 * - Exposición de props para componentes de vista
 * - Detección de estados de filtros activos
 * - Conversión de FilterConditionOutput[] a advanced_filters de la API
 */

import { FilterConditionOutput } from '@components/ui/AdvancedFilter';
import { useMemo } from 'react';
import { useEntries } from './useEntries';

/**
 * Mapeo de operadores del frontend a operadores de la API
 */
const ACTION_TO_OPERATOR_MAP: Record<string, string> = {
  equals: 'equals',
  not_equals: 'not_equals',
  contains: 'contains',
  not_contains: 'not_contains',
  starts_with: 'starts_with',
  ends_with: 'ends_with',
  greater_than: 'greater_than',
  less_than: 'less_than',
  greater_equal: 'greater_equal',
  less_equal: 'less_equal',
  in: 'in',
  not_in: 'not_in',
  is_null: 'is_null',
  is_not_null: 'is_not_null',
};

const PRODUCT_FILTER_COLUMNS = new Set(['product_ids', 'product_id']);

/**
 * Extrae IDs de producto del estado de filtros UI.
 * La API espera product_ids como query param directo, no en advanced_filters.
 */
function extractProductIds(conditions: FilterConditionOutput[]): number[] {
  const productCondition = conditions.find((c) => PRODUCT_FILTER_COLUMNS.has(c.column) && c.action === 'in');

  if (!productCondition?.value) {
    return [];
  }

  const values = Array.isArray(productCondition.value) ? productCondition.value : [productCondition.value];

  return values.map(Number).filter((id) => !Number.isNaN(id));
}

/**
 * Convierte FilterConditionOutput[] al formato advanced_filters de la API
 */
function convertToAdvancedFilters(conditions: FilterConditionOutput[]) {
  const advancedConditions = conditions.filter((c) => !PRODUCT_FILTER_COLUMNS.has(c.column));

  if (!advancedConditions || advancedConditions.length === 0) {
    return undefined;
  }

  const filters = advancedConditions.map((condition) => ({
    column: condition.column,
    operator: ACTION_TO_OPERATOR_MAP[condition.action] || condition.action,
    value: condition.value,
  }));

  return {
    logic: 'AND' as const,
    filters,
  };
}

export const useEntriesMain = (
  searchTerm?: string,
  externalFilterConditions?: FilterConditionOutput[],
  externalSorting?: { field: string; direction: 'asc' | 'desc' } | null,
) => {
  // Procesar filtros para enviar al hook
  const processedFilters = useMemo(() => {
    const currentSorting = externalSorting;

    return {
      sortBy: currentSorting?.field ?? 'id',
      sortDirection: currentSorting?.direction ?? 'desc',
      searchTerm: searchTerm || '',
    };
  }, [externalSorting, searchTerm]);

  const filterConditions = externalFilterConditions || [];

  // Convertir condiciones de filtro al formato de la API
  const advancedFilters = useMemo(() => convertToAdvancedFilters(filterConditions), [filterConditions]);

  const productIds = useMemo(() => extractProductIds(filterConditions), [filterConditions]);

  // Memoizar el objeto de filtros para evitar re-renders infinitos
  const queryFilters = useMemo(
    () => ({
      ...processedFilters,
      sorting: {
        column: (processedFilters.sortBy as 'id' | 'reference' | 'created_at' | 'estimated_delivery_at') || 'id',
        direction: (processedFilters.sortDirection as 'asc' | 'desc') || 'desc',
      },
      advanced_filters: advancedFilters,
      ...(productIds.length > 0 ? { product_ids: productIds } : {}),
    }),
    [processedFilters, advancedFilters, productIds],
  );

  // Hook de datos con scroll infinito
  const { entries, isLoading, isFetching, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, total } =
    useEntries(queryFilters);

  // Props compartidas para ambas vistas (Mobile/Desktop)
  const entriesViewProps = useMemo(
    () => ({
      entries,
      isLoading,
      isFetching,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
      refetch,
      sorting: externalSorting,
    }),
    [entries, isLoading, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage, total, refetch, externalSorting],
  );

  // Detectar si hay filtros activos
  const hasActiveFilters = useMemo(() => {
    const currentFilterConditions = externalFilterConditions || [];
    const currentSearchTerm = searchTerm || '';
    const currentSorting = externalSorting;

    return currentFilterConditions.length > 0 || currentSearchTerm !== '' || currentSorting !== null;
  }, [externalFilterConditions, searchTerm, externalSorting]);

  return {
    // Estados
    filterConditions: externalFilterConditions || [],
    searchTerm: searchTerm || '',
    sorting: externalSorting,

    // Datos
    entries,
    isLoading,
    isFetching,
    error,
    total,

    // Props para componentes
    entriesViewProps,

    // Handlers
    refetch,

    // Props para infinite scroll
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

    // Estado de filtros
    hasActiveFilters,
  };
};
